/**
 * DateRangePicker — two months side by side, a preset rail, and a mono trigger.
 *
 * Presets carry the same keys the analytics API uses (`7d`, `30d`, `12m`, `ytd`)
 * so a screen can hand the chosen key straight to `/analytics/*` instead of
 * re-deriving it from two dates.
 */

import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isSameYear,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subYears,
} from 'date-fns';
import { CalendarRange, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { ISODate } from '@shared/types';
import { cn } from '@/lib/cn';
import { composeRefs } from './Button';
import type { ControlSize } from './Button';
import { FIELD_PAD, FIELD_TEXT, fieldShell } from './Input';
import {
  CalendarMonth,
  PRESET_ITEM,
  PRESET_RAIL,
  parseISODate,
  toISODate,
} from './DatePicker';
import type { WeekStart } from './DatePicker';

export interface DateRange {
  from: ISODate;
  to: ISODate;
}

export interface DateRangePreset {
  /** Matches `RangeKey` where one exists, so it can be forwarded to the API. */
  key: string;
  label: string;
  /** `null` marks the "Custom" rail entry — it selects nothing on its own. */
  get: (today: Date) => { from: Date; to: Date } | null;
}

export const DEFAULT_RANGE_PRESETS: DateRangePreset[] = [
  { key: 'today', label: 'Today', get: (t) => ({ from: t, to: t }) },
  { key: '7d', label: 'Last 7 days', get: (t) => ({ from: subDays(t, 6), to: t }) },
  { key: '30d', label: 'Last 30 days', get: (t) => ({ from: subDays(t, 29), to: t }) },
  { key: '90d', label: 'Last 90 days', get: (t) => ({ from: subDays(t, 89), to: t }) },
  { key: '12m', label: 'Last 12 months', get: (t) => ({ from: addDays(subYears(t, 1), 1), to: t }) },
  { key: 'ytd', label: 'Year to date', get: (t) => ({ from: startOfYear(t), to: t }) },
  { key: 'custom', label: 'Custom', get: () => null },
];

/**
 * The LEFT panel's month, given the month the range ends in.
 *
 * One behind, so the pair reads `July + August` for a range ending 7 August
 * rather than `August + September`. Anchoring the left panel on the end month
 * put the right-hand panel entirely in the future — half the popover's surface
 * spent on dates that cannot be in a past-facing range, and on a year-to-date
 * window the only shaded cells were the twelve at the very end of the left
 * panel. One month back keeps the end date in context, brings the run-up to it
 * on screen, and leaves no panel that the reader can do nothing with. Paging is
 * one click either way, and `handleFocusedDateChange` already pulls the view to
 * whichever month the keyboard walks into.
 */
function leftMonthFor(rangeEnd: Date): Date {
  return startOfMonth(addMonths(rangeEnd, -1));
}

/** `Jun 12 – Jul 11, 2026` · `Dec 12, 2025 – Jul 11, 2026` · `Jul 11, 2026`. */
export function formatRangeLabel(from: Date, to: Date): string {
  if (isSameDay(from, to)) return format(from, 'MMM d, yyyy');
  if (isSameYear(from, to)) return `${format(from, 'MMM d')} – ${format(to, 'MMM d, yyyy')}`;
  return `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
}

export interface DateRangePickerProps {
  value?: DateRange | null;
  onChange?: (value: DateRange | null) => void;
  /** Fires alongside `onChange` with the rail key — `30d`, `ytd`, `custom`. */
  onPresetChange?: (key: string) => void;
  presets?: DateRangePreset[];
  placeholder?: string;
  clearable?: boolean;
  size?: ControlSize;
  invalid?: boolean;
  disabled?: boolean;
  min?: ISODate | null;
  max?: ISODate | null;
  weekStartsOn?: WeekStart;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Overrides "now" so screenshots stay reproducible. */
  today?: Date;
  footer?: ReactNode;
}

export const DateRangePicker = forwardRef<HTMLButtonElement, DateRangePickerProps>(
  function DateRangePicker(
    {
      value,
      onChange,
      onPresetChange,
      presets = DEFAULT_RANGE_PRESETS,
      placeholder = 'Select range',
      clearable = true,
      size = 'md',
      invalid = false,
      disabled = false,
      min,
      max,
      weekStartsOn = 1,
      id,
      className,
      align = 'start',
      side = 'bottom',
      today: todayProp,
      footer,
      ...aria
    },
    ref,
  ) {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const today = useMemo(() => startOfDay(todayProp ?? new Date()), [todayProp]);
    const minDate = useMemo(() => parseISODate(min), [min]);
    const maxDate = useMemo(() => parseISODate(max), [max]);

    const committed = useMemo(() => {
      const from = parseISODate(value?.from);
      const to = parseISODate(value?.to);
      return from && to ? { from, to } : null;
    }, [value?.from, value?.to]);

    const [open, setOpen] = useState(false);
    /** Half-finished selection: `to` is null between the two clicks. */
    const [draft, setDraft] = useState<{ from: Date; to: Date | null } | null>(null);
    const [hovered, setHovered] = useState<Date | null>(null);
    const [viewMonth, setViewMonth] = useState<Date>(() =>
      leftMonthFor(committed ? committed.to : today),
    );
    const [focusedDate, setFocusedDate] = useState<Date>(() => committed?.to ?? today);
    const [focusToken, setFocusToken] = useState(0);

    const activePresetKey = useMemo(() => {
      if (!committed) return null;
      for (const preset of presets) {
        const resolved = preset.get(today);
        if (
          resolved &&
          isSameDay(resolved.from, committed.from) &&
          isSameDay(resolved.to, committed.to)
        ) {
          return preset.key;
        }
      }
      return 'custom';
    }, [committed, presets, today]);

    const rightMonth = useMemo(() => addMonths(viewMonth, 1), [viewMonth]);

    const handleOpenChange = useCallback(
      (next: boolean) => {
        setOpen(next);
        if (next) {
          setDraft(null);
          setHovered(null);
          const anchor = committed?.to ?? today;
          setViewMonth(leftMonthFor(anchor));
          setFocusedDate(anchor);
          setFocusToken((token) => token + 1);
        }
      },
      [committed, today],
    );

    const commitRange = useCallback(
      (from: Date, to: Date, presetKey: string) => {
        onChange?.({ from: toISODate(from), to: toISODate(to) });
        onPresetChange?.(presetKey);
      },
      [onChange, onPresetChange],
    );

    const handleSelect = useCallback(
      (day: Date) => {
        if (!draft || draft.to !== null) {
          setDraft({ from: day, to: null });
          setHovered(null);
          return;
        }
        const from = isBefore(day, draft.from) ? day : draft.from;
        const to = isBefore(day, draft.from) ? draft.from : day;
        setDraft({ from, to });
        commitRange(from, to, 'custom');
        setOpen(false);
        triggerRef.current?.focus();
      },
      [commitRange, draft],
    );

    const handleFocusedDateChange = useCallback(
      (date: Date) => {
        setFocusedDate(date);
        setFocusToken((token) => token + 1);
        setViewMonth((current) => {
          const left = startOfMonth(current);
          const right = endOfMonth(addMonths(current, 1));
          if (isBefore(date, left)) return startOfMonth(date);
          if (isAfter(date, right)) return startOfMonth(addMonths(date, -1));
          return current;
        });
      },
      [],
    );

    const displayRange = draft
      ? { from: draft.from, to: draft.to }
      : committed
        ? { from: committed.from, to: committed.to }
        : null;

    const label = committed ? formatRangeLabel(committed.from, committed.to) : placeholder;

    const focusOwnerIsLeft = isSameMonth(focusedDate, viewMonth) || !isSameMonth(focusedDate, rightMonth);

    return (
      <RPopover.Root open={open} onOpenChange={handleOpenChange} modal={false}>
        <RPopover.Trigger asChild>
          <button
            ref={composeRefs(ref, triggerRef)}
            type="button"
            id={id}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            onKeyDown={(event) => {
              // The clear affordance inside the trigger cannot be focusable (a
              // button inside a button is invalid markup), so the key clears.
              if (clearable && committed && (event.key === 'Backspace' || event.key === 'Delete')) {
                event.preventDefault();
                onChange?.(null);
              }
            }}
            className={cn(
              'group',
              /* `focus: 'none'` — this trigger sits in a toolbar beside plain
                 buttons, and an inset 1px ring where the control next to it
                 draws the 2px offset outline is two focus idioms 6px apart. The
                 base `:focus-visible` rule is the product's one focus mark; the
                 open-state border below is a *state* cue and stays. */
              fieldShell({ size, invalid, disabled, focus: 'none' }),
              FIELD_PAD[size],
              'justify-start text-left',
              'data-[state=open]:border-focus data-[state=open]:shadow-[inset_0_0_0_1px_var(--focus)]',
              className,
            )}
            {...aria}
          >
            <CalendarRange aria-hidden="true" className="size-3.5 shrink-0 text-muted" />
            <span
              /* `FIELD_TEXT[size]`, not a fixed `text-sm`. The trigger stands in
                 a toolbar next to 13px/500 buttons; hard-coding 12px put a type
                 step on that 40px row that the rest of the row — and almost the
                 rest of the page — does not use. A md field is 13px everywhere
                 else in the product, and `font-medium` matches the buttons it
                 sits beside so the row runs on one size and one weight. */
              className={cn(
                'min-w-0 flex-1 truncate font-mono font-medium tnum',
                FIELD_TEXT[size],
                committed ? 'text-primary' : 'text-muted',
              )}
            >
              {label}
            </span>
            {clearable && committed ? (
              <span
                aria-hidden="true"
                title="Clear range (Backspace)"
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange?.(null);
                }}
                className="grid size-4 shrink-0 place-items-center rounded-xs text-muted hover:bg-hover hover:text-primary"
              >
                <X className="size-3" />
              </span>
            ) : null}
          </button>
        </RPopover.Trigger>

        <RPopover.Portal>
          <RPopover.Content
            align={align}
            side={side}
            sideOffset={4}
            onOpenAutoFocus={(event) => event.preventDefault()}
            className="z-[var(--z-popover)] flex overflow-hidden rounded-md border border-line bg-surface-overlay shadow-3"
            style={{ animation: 'af-scale-in var(--dur-fast) var(--ease-out)' }}
          >
            <div className={PRESET_RAIL} role="group" aria-label="Range presets">
              {presets.map((preset) => {
                const active = preset.key === activePresetKey;
                return (
                  <button
                    key={preset.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      const resolved = preset.get(today);
                      if (!resolved) {
                        setDraft(null);
                        onPresetChange?.(preset.key);
                        setFocusToken((token) => token + 1);
                        return;
                      }
                      setDraft(null);
                      setViewMonth(leftMonthFor(resolved.to));
                      setFocusedDate(resolved.to);
                      commitRange(resolved.from, resolved.to, preset.key);
                      setOpen(false);
                      triggerRef.current?.focus();
                    }}
                    className={cn(
                      PRESET_ITEM,
                      'justify-between gap-2',
                      active && 'bg-selected text-primary',
                    )}
                  >
                    <span className="truncate">{preset.label}</span>
                    {/* `--text-primary`, not gold: a menu tick is a state marker
                        and a state marker is marked by its presence. The row it
                        sits on already carries `bg-selected`, and gold on it
                        made the brand mark mean "create" on the Save view button
                        and "this preset is on" 200px below it. */}
                    {active ? (
                      <Check aria-hidden="true" className="size-3.5 shrink-0 text-primary" />
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div className="p-2">
              <div className="relative">
                <div className="absolute inset-x-0 top-0 flex h-7 items-center justify-between">
                  <button
                    type="button"
                    aria-label="Previous month"
                    onClick={() => setViewMonth((month) => addMonths(month, -1))}
                    className="grid size-6 place-items-center rounded-xs! text-muted hover:bg-hover hover:text-primary"
                  >
                    <ChevronLeft aria-hidden="true" className="size-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next month"
                    onClick={() => setViewMonth((month) => addMonths(month, 1))}
                    className="grid size-6 place-items-center rounded-xs! text-muted hover:bg-hover hover:text-primary"
                  >
                    <ChevronRight aria-hidden="true" className="size-4" />
                  </button>
                </div>

                <div className="flex gap-4">
                  <CalendarMonth
                    month={viewMonth}
                    range={displayRange}
                    preview={draft && draft.to === null ? hovered : null}
                    focusedDate={focusedDate}
                    onFocusedDateChange={handleFocusedDateChange}
                    onSelect={handleSelect}
                    onHoverDate={setHovered}
                    min={minDate}
                    max={maxDate}
                    weekStartsOn={weekStartsOn}
                    today={today}
                    isFocusOwner={focusOwnerIsLeft}
                    focusToken={focusToken}
                  />
                  <CalendarMonth
                    month={rightMonth}
                    range={displayRange}
                    preview={draft && draft.to === null ? hovered : null}
                    focusedDate={focusedDate}
                    onFocusedDateChange={handleFocusedDateChange}
                    onSelect={handleSelect}
                    onHoverDate={setHovered}
                    min={minDate}
                    max={maxDate}
                    weekStartsOn={weekStartsOn}
                    today={today}
                    isFocusOwner={!focusOwnerIsLeft}
                    focusToken={focusToken}
                  />
                </div>
              </div>

              {footer ?? (
                <div className="mt-2 flex items-center justify-between border-t border-line-subtle pt-2">
                  <span className="font-mono text-xs text-muted tnum">
                    {draft && draft.to === null
                      ? `${format(draft.from, 'MMM d, yyyy')} – …`
                      : committed
                        ? formatRangeLabel(committed.from, committed.to)
                        : 'No range selected'}
                  </span>
                  {clearable ? (
                    <button
                      type="button"
                      onClick={() => {
                        setDraft(null);
                        onChange?.(null);
                        setOpen(false);
                      }}
                      className="rounded-xs! px-1.5 py-1 text-sm text-muted hover:bg-hover hover:text-primary"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
  },
);
